import { getValue } from "aldoo-ra/Value"

// Shared with invoice-step.jsx / checkout-total-bar.jsx
export const INVOICE_FORM_KEY = "checkout-invoice-form-data"

/**
 * Built-in purchase flow steps.
 *
 * Every step is registered in the step registry (see StorePurchaseFlow
 * registerStep) so a project flow can reference it by id — e.g.
 * `steps: ["email", "billing", "payment", "success"]` — and only has to
 * define the steps it adds.
 *
 * A step definition:
 * - id: unique step id
 * - name: label in the progress header (string or () => string)
 * - component: element / fragment id rendered for the step (custom steps)
 * - title: modal title while the step is active (string or () => string)
 * - buttonText: label of the Continue button
 * - skipCondition(context): true to skip the step; `condition(context)` is the
 *   positive form (show when true) — either may be used
 * - requiresPaymentInfo: the step needs store.paymentInfo before it renders
 *   (default: every step except "email")
 * - refreshPayment: re-run the flow's initializePayment when the step is entered
 * - isPayment: the Continue button is a "Pay" action (wallet icon, pay-button test id)
 * - hideInProgress: never listed in the progress header
 * - alwaysShow: always listed in the progress header
 * - layout: "centered" to vertically center the step content
 * - stripe: wrap the step in a Stripe <Elements> bound to the current payment
 */
export const cartStep = {
  id: "cart",
  name: "Shopping Cart",
  component: "shopping-cart",
  skipCondition: () => {
    // Always skip cart in checkout flow - cart is shown in separate modal
    return true
  },
}

export const emailStep = {
  id: "email",
  name: "Email",
  component: "email-input-dialog",
  buttonText: "Continue",
  requiresPaymentInfo: false,
  skipCondition: (context) => {
    // Skip if user is logged in or has temp auth with email
    const hasEmail = context.auth?.email || context.tempAuthForPurchase?.email || context.purchaseUserEmail
    return !!hasEmail
  },
}

export const invoiceStep = {
  id: "invoice",
  name: "Invoice",
  component: "invoice-step",
  buttonText: "Continue",
  skipCondition: () => {
    // Opt-in only. Most buyers never need an invoice, and asking everyone
    // added a screen between email and payment. The "Need an invoice?"
    // link in the payment step's total bar sets this flag synchronously
    // (setValue) right before publishing PURCHASE_FLOW_GOTO, so it is
    // readable here.
    const invoiceForm = getValue(INVOICE_FORM_KEY)
    return !invoiceForm?.needsInvoice
  },
}

export const paymentReuseStep = {
  id: "payment-reuse",
  name: "Payment",
  component: "reuse-payment-dialog",
  buttonText: "Pay now",
  isPayment: true,
  skipCondition: (context) => {
    // Show reuse dialog ONLY if:
    // 1. paymentInfo is loaded AND
    // 2. User has a saved payment method
    // This is the final step for users with everything set up
    if (!context.paymentInfo) return true
    if (!context.paymentInfo.savedPaymentMethod) return true
    return false
  },
}

export const paymentStep = {
  id: "payment",
  name: "Payment",
  component: "checkout-form",
  buttonText: "Pay now",
  isPayment: true,
  skipCondition: (context) => {
    // Don't skip if user explicitly chose to add new payment method
    if (context.useNewPaymentMethod === true) {
      return false
    }

    // Skip payment form if user has saved payment method
    // (they'll go to reuse dialog instead)
    // Show payment form for users without saved payment
    if (!context.paymentInfo) return false
    const shouldSkip = !!context.paymentInfo.savedPaymentMethod
    return shouldSkip
  },
  alwaysShow: true, // Always show in progress header
}

export const successStep = {
  id: "success",
  name: "Payment Success",
  component: "payment-success",
  skipCondition: null,
  // Success screen shown after payment completion
}

export const builtInSteps = [cartStep, emailStep, invoiceStep, paymentReuseStep, paymentStep, successStep]
